import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Save } from 'lucide-react';
import { Promotion, PromotionStatus } from './types';
interface PromotionFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (promotion: Omit<Promotion, 'id'> | Promotion) => void;
  initialData?: Promotion;
}
export function PromotionForm({
  isOpen,
  onClose,
  onSave,
  initialData
}: PromotionFormProps) {
  const [formData, setFormData] = useState<Partial<Promotion>>({
    bankName: '',
    promotionName: '',
    minAmount: 0,
    deadline: new Date().toISOString().split('T')[0],
    bonusDate: '',
    bonusAmount: 0,
    status: 'active',
    notes: '',
    accountType: 'Checking'
  });
  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    } else {
      setFormData({
        bankName: '',
        promotionName: '',
        minAmount: 0,
        deadline: new Date().toISOString().split('T')[0],
        bonusDate: '',
        bonusAmount: 0,
        status: 'active',
        notes: '',
        accountType: 'Checking'
      });
    }
  }, [initialData, isOpen]);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData as Promotion);
    onClose();
  };
  return <AnimatePresence data-id="element-105">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40" data-id="element-106" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none" data-id="element-107">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden pointer-events-auto flex flex-col max-h-[90vh]" data-id="element-108">
              <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50" data-id="element-109">
                <h2 className="text-lg font-bold text-gray-900" data-id="element-110">
                  {initialData ? 'Edit Promotion' : 'New Promotion'}
                </h2>
                <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors" data-id="element-111">
                  <X className="w-5 h-5" data-id="element-112" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 overflow-y-auto space-y-4" data-id="element-113">
                <div className="grid grid-cols-2 gap-4" data-id="element-114">
                  <div className="space-y-1" data-id="element-115">
                    <label className="text-xs font-medium text-gray-700 uppercase tracking-wide" data-id="element-116">
                      Bank Name
                    </label>
                    <input required type="text" value={formData.bankName} onChange={e => setFormData({
                  ...formData,
                  bankName: e.target.value
                })} className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" placeholder="e.g. Chase" data-id="element-117" />
                  </div>
                  <div className="space-y-1" data-id="element-118">
                    <label className="text-xs font-medium text-gray-700 uppercase tracking-wide" data-id="element-119">
                      Account Type
                    </label>
                    <select value={formData.accountType} onChange={e => setFormData({
                  ...formData,
                  accountType: e.target.value as any
                })} className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white" data-id="element-120">
                      <option value="Checking" data-id="element-121">Checking</option>
                      <option value="Savings" data-id="element-122">Savings</option>
                      <option value="CD" data-id="element-123">CD</option>
                      <option value="Credit Card" data-id="element-124">Credit Card</option>
                      <option value="Brokerage" data-id="element-125">Brokerage</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-1" data-id="element-126">
                  <label className="text-xs font-medium text-gray-700 uppercase tracking-wide" data-id="element-127">
                    Promotion Name
                  </label>
                  <input required type="text" value={formData.promotionName} onChange={e => setFormData({
                ...formData,
                promotionName: e.target.value
              })} className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" placeholder="e.g. Total Checking $300 Bonus" data-id="element-128" />
                </div>

                <div className="grid grid-cols-2 gap-4" data-id="element-129">
                  <div className="space-y-1" data-id="element-130">
                    <label className="text-xs font-medium text-gray-700 uppercase tracking-wide" data-id="element-131">
                      Bonus Amount ($)
                    </label>
                    <input required type="number" min="0" value={formData.bonusAmount} onChange={e => setFormData({
                  ...formData,
                  bonusAmount: Number(e.target.value)
                })} className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none font-mono" data-id="element-132" />
                  </div>
                  <div className="space-y-1" data-id="element-133">
                    <label className="text-xs font-medium text-gray-700 uppercase tracking-wide" data-id="element-134">
                      Min Deposit ($)
                    </label>
                    <input required type="number" min="0" value={formData.minAmount} onChange={e => setFormData({
                  ...formData,
                  minAmount: Number(e.target.value)
                })} className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none font-mono" data-id="element-135" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4" data-id="element-136">
                  <div className="space-y-1" data-id="element-137">
                    <label className="text-xs font-medium text-gray-700 uppercase tracking-wide" data-id="element-138">
                      Deadline
                    </label>
                    <input required type="date" value={formData.deadline} onChange={e => setFormData({
                  ...formData,
                  deadline: e.target.value
                })} className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none font-mono text-sm" data-id="element-139" />
                  </div>
                  <div className="space-y-1" data-id="element-140">
                    <label className="text-xs font-medium text-gray-700 uppercase tracking-wide" data-id="element-141">
                      Bonus Pay Date
                    </label>
                    <input type="date" value={formData.bonusDate} onChange={e => setFormData({
                  ...formData,
                  bonusDate: e.target.value
                })} className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none font-mono text-sm" data-id="element-142" />
                  </div>
                </div>

                <div className="space-y-1" data-id="element-143">
                  <label className="text-xs font-medium text-gray-700 uppercase tracking-wide" data-id="element-144">
                    Status
                  </label>
                  <select value={formData.status} onChange={e => setFormData({
                ...formData,
                status: e.target.value as PromotionStatus
              })} className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white" data-id="element-145">
                    <option value="active" data-id="element-146">Active (Researching)</option>
                    <option value="applied" data-id="element-147">Applied</option>
                    <option value="pending" data-id="element-148">Bonus Pending</option>
                    <option value="completed" data-id="element-149">Completed</option>
                    <option value="expired" data-id="element-150">Expired</option>
                  </select>
                </div>

                <div className="space-y-1" data-id="element-151">
                  <label className="text-xs font-medium text-gray-700 uppercase tracking-wide" data-id="element-152">
                    Notes
                  </label>
                  <textarea rows={3} value={formData.notes} onChange={e => setFormData({
                ...formData,
                notes: e.target.value
              })} className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all resize-none" placeholder="Requirements, login details, etc." data-id="element-153" />
                </div>

                <div className="pt-4 flex justify-end gap-3" data-id="element-154">
                  <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500" data-id="element-155">
                    Cancel
                  </button>
                  <button type="submit" className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center gap-2" data-id="element-156">
                    <Save className="w-4 h-4" data-id="element-157" />
                    Save Promotion
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}